import {
  calendarId as makeCalendarId,
  minutes,
  type ISODateTime,
  type Minutes,
  type TaskId,
} from '../domain/branded.js';
import type { Calendar, WorkingTime } from '../domain/calendar.js';
import type { Assignment } from '../domain/assignment.js';
import type { Resource } from '../domain/resource.js';
import type { Dependency } from '../domain/dependency.js';
import type { Project } from '../domain/project.js';
import type { Task } from '../domain/task.js';
import type { Diagnostic } from '../domain/errors.js';
import { rollUpSummaryPercent } from './task-progress.js';
import {
  ConstraintConflictError,
  EntityNotFoundError,
  InvalidArgumentError,
} from '../domain/errors.js';
import {
  addWorkingMinutes,
  intersectWorkingCalendars,
  resolveCalendar,
  snapToNextWorkingTime,
  snapToPreviousWorkingTime,
  subtractWorkingMinutes,
  workingMinutesBetween,
  type ResolvedCalendar,
} from './calendar-engine.js';
import { buildScheduleGraph, type ScheduleGraph } from './scheduling-graph.js';

export type { TaskEffort, EffortChange } from './scheduling-task-type.js';
export { recalculateEffort } from './scheduling-task-type.js';
export { buildScheduleGraph } from './scheduling-graph.js';
export type { ScheduleGraph } from './scheduling-graph.js';

/**
 * 排程引擎契約（CPM 正向／逆向計算）。
 * 實作者：scheduling agent。此檔只定義輸入／輸出，不含演算法。
 */

export interface ScheduleOptions {
  /** 覆寫 `Project.scheduleFrom`；未提供則沿用專案設定。 */
  readonly scheduleFrom?: Project['scheduleFrom'];
  /** 覆寫排程錨點日期；未提供則沿用 `Project.startDate` / `finishDate`。 */
  readonly anchorDate?: ISODateTime;
  /** 硬性限制（MSO / MFO / SNLT / FNLT）是否可覆寫相依關係。MS Project 預設 true。 */
  readonly honourConstraints?: boolean;
  /** 是否允許限制條件造成負浮時（否則遇衝突拋 ConstraintConflictError）。 */
  readonly allowNegativeFloat?: boolean;
  /** 視為要徑的總浮時門檻，預設 0 分鐘。 */
  readonly criticalSlackThreshold?: Minutes;
  /** 是否重新計算摘要任務的彙總值。預設 true。 */
  readonly rollupSummaries?: boolean;
  /** 只重算這些任務及其下游（增量排程）；未提供則全量重算。 */
  readonly dirtyTaskIds?: readonly TaskId[];
}

export interface ScheduleInput {
  readonly project: Project;
  readonly options?: ScheduleOptions;
}

/** 單一任務的排程計算結果（不含未變動的靜態欄位）。 */
export interface ScheduledTask {
  readonly taskId: TaskId;
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
  readonly earlyStart: ISODateTime;
  readonly earlyFinish: ISODateTime;
  readonly lateStart: ISODateTime;
  readonly lateFinish: ISODateTime;
  /**
   * 總浮時 = late start − early start（工作分鐘）。
   * 引擎回傳原始值，硬性限制或 deadline 造成無解時會是負值。
   */
  readonly totalFloat: Minutes;
  /**
   * 自由浮時：在不推遲任何後續任務最早開始（FF/SF 則為最早完成）的前提下，
   * 本任務可延後的工作分鐘數。定義為
   * `min over successors( workingMinutesBetween(依相依類型與 lag 推得的最早允許時點, successor 的 ES/EF) )`；
   * 無後續任務時等於 `totalFloat`，且一律再取 `min(freeFloat, totalFloat)`。
   *
   * **引擎回傳原始值，可為負**（硬性限制或 deadline 造成無解時）。這是刻意的：
   * 資源撫平以浮時排序，截斷成 0 會讓「沒有延後空間」與「已經超期」變得無法區分。
   * MS Project 顯示 0 是呈現層行為 —— UI 需自行 `Math.max(0, x)`，引擎不做截斷。
   */
  readonly freeFloat: Minutes;
  readonly isCritical: boolean;
  /** 實際排入的工作分鐘數（扣除非工作時間）。 */
  readonly durationMinutes: Minutes;
  /** 任務被切分時的各段區間；未切分則為單一段。 */
  readonly segments: readonly TaskSegment[];
}

export interface TaskSegment {
  readonly start: ISODateTime;
  readonly finish: ISODateTime;
}

export interface ScheduleResult {
  readonly tasks: readonly ScheduledTask[];
  readonly projectStart: ISODateTime;
  readonly projectFinish: ISODateTime;
  /** 要徑上的任務 id（依時間先後排序）。可能有多條要徑，此處為聯集。 */
  readonly criticalPath: readonly TaskId[];
  /** 超過 deadline 的任務。 */
  readonly deadlineViolations: readonly TaskId[];
  readonly diagnostics: readonly Diagnostic[];
}

// ---------------------------------------------------------------------------
// 時間點比較（wall-clock 字串，分鐘粒度；不做任何日期運算）
// ---------------------------------------------------------------------------

/** 取到分鐘為止的可比較鍵（`YYYY-MM-DDTHH:mm`）。秒被忽略，與日曆引擎一致。 */
const cmpKey = (at: ISODateTime): string => (at as string).slice(0, 16);

const isBefore = (a: ISODateTime, b: ISODateTime): boolean => cmpKey(a) < cmpKey(b);
const isAfter = (a: ISODateTime, b: ISODateTime): boolean => cmpKey(a) > cmpKey(b);
const isSame = (a: ISODateTime, b: ISODateTime): boolean => cmpKey(a) === cmpKey(b);
const later = (a: ISODateTime, b: ISODateTime): ISODateTime => (isBefore(a, b) ? b : a);
const earlier = (a: ISODateTime, b: ISODateTime): ISODateTime => (isAfter(a, b) ? b : a);

// ---------------------------------------------------------------------------
// 24×7「日曆時間」日曆：供 lagIsElapsed 使用，避免自行做日期運算
// ---------------------------------------------------------------------------

const FULL_DAY: readonly WorkingTime[] = [{ start: minutes(0), end: minutes(1440) }];

const ELAPSED_CALENDAR: ResolvedCalendar = {
  id: makeCalendarId('__elapsed__'),
  workingTimesOn: () => FULL_DAY,
  isWorkingDay: () => true,
  isWorkingTime: () => true,
};

// ---------------------------------------------------------------------------
// 內部狀態
// ---------------------------------------------------------------------------

interface TaskState {
  readonly task: Task;
  readonly calendar: ResolvedCalendar;
  readonly isSummary: boolean;
  readonly children: readonly Task[];
  earlyStart: ISODateTime;
  earlyFinish: ISODateTime;
  lateStart: ISODateTime;
  lateFinish: ISODateTime;
  start: ISODateTime;
  finish: ISODateTime;
  totalFloat: number;
  freeFloat: number;
  isCritical: boolean;
}

const HARD_FROM_START = new Set(['MSO', 'MFO', 'SNLT', 'FNLT']);

/**
 * 執行 CPM 排程。
 * @throws {CircularDependencyError} 相依關係成環時。
 * @throws {ConstraintConflictError} 限制條件無法滿足且 allowNegativeFloat 為 false 時。
 */
export function schedule(input: ScheduleInput): ScheduleResult {
  const project = input.project;
  const options = input.options ?? {};
  const scheduleFrom = options.scheduleFrom ?? project.scheduleFrom;
  const honourConstraints = options.honourConstraints ?? true;
  const allowNegativeFloat = options.allowNegativeFloat ?? true;
  const criticalThreshold = (options.criticalSlackThreshold ?? minutes(0)) as number;
  const rollupSummaries = options.rollupSummaries ?? true;
  const diagnostics: Diagnostic[] = [];

  if (options.dirtyTaskIds !== undefined && options.dirtyTaskIds.length > 0) {
    diagnostics.push({
      severity: 'Info',
      code: 'INCREMENTAL_SCHEDULING_UNSUPPORTED',
      message:
        'dirtyTaskIds was provided but incremental scheduling is not implemented; a full recalculation was performed instead.',
    });
  }

  const graph: ScheduleGraph = buildScheduleGraph(project);

  // --- 日曆解析（每個 calendarId 只解析一次）---
  const calendarsById = new Map<string, Calendar>();
  for (const calendar of project.calendars) calendarsById.set(calendar.id as string, calendar);
  const resolvedCache = new Map<string, ResolvedCalendar>();
  const resolveById = (id: string): ResolvedCalendar => {
    const cached = resolvedCache.get(id);
    if (cached !== undefined) return cached;
    const calendar = calendarsById.get(id);
    if (calendar === undefined) throw new EntityNotFoundError('CALENDAR_NOT_FOUND', id);
    const resolved = resolveCalendar(calendar, calendarsById);
    resolvedCache.set(id, resolved);
    return resolved;
  };

  const anchorDate =
    options.anchorDate ?? (scheduleFrom === 'ProjectFinish' ? project.finishDate : project.startDate);

  if (project.tasks.length === 0) {
    return {
      tasks: [],
      projectStart: anchorDate,
      projectFinish: anchorDate,
      criticalPath: [],
      deadlineViolations: [],
      diagnostics,
    };
  }

  const projectCalendar = resolveById(project.defaultCalendarId as string);
  const resourcesById = new Map<string, Resource>();
  for (const resource of project.resources) resourcesById.set(resource.id as string, resource);
  const assignmentsByTask = new Map<string, Assignment[]>();
  for (const assignment of project.assignments) {
    const bucket = assignmentsByTask.get(assignment.taskId as string);
    if (bucket === undefined) assignmentsByTask.set(assignment.taskId as string, [assignment]);
    else bucket.push(assignment);
  }
  const calendarFor = (task: Task): ResolvedCalendar => {
    const taskCalendar = task.calendarId === null ? projectCalendar : resolveById(task.calendarId as string);
    if (task.ignoreResourceCalendar) return taskCalendar;
    const calendars: ResolvedCalendar[] = [taskCalendar];
    for (const assignment of assignmentsByTask.get(task.id as string) ?? []) {
      const resource = resourcesById.get(assignment.resourceId as string);
      if (resource?.type !== 'Work') continue;
      calendars.push(resource.calendarId === null ? projectCalendar : resolveById(resource.calendarId as string));
    }
    return intersectWorkingCalendars(calendars);
  };

  // --- 初始化狀態 ---
  const states = new Map<string, TaskState>();
  for (const task of project.tasks) {
    const children = graph.childrenByParent.get(task.id as string) ?? [];
    states.set(task.id as string, {
      task,
      calendar: calendarFor(task),
      isSummary: children.length > 0,
      children,
      earlyStart: task.start,
      earlyFinish: task.finish,
      lateStart: task.start,
      lateFinish: task.finish,
      start: task.start,
      finish: task.finish,
      totalFloat: 0,
      freeFloat: 0,
      isCritical: false,
    });
  }
  const stateOf = (id: string): TaskState => {
    const state = states.get(id);
    if (state === undefined) throw new EntityNotFoundError('TASK_NOT_FOUND', id);
    return state;
  };

  // --- lag 位移（一律走日曆引擎；elapsed lag 走 24×7 日曆）---
  const lagMinutesOf = (dep: Dependency): number => {
    const raw = dep.lagMinutes as number;
    if (dep.lagIsPercent !== true) return raw;
    // 契約：lagMinutes 為「前置任務工期的百分比 × 100」，故 5000 = 50%。
    // 與 io/mspdi 對齊：MSPDI 的 <LinkLag> 在 LagFormat=19/20（百分比）時以「十分之一個
    // 百分點」儲存，import.ts 會乘以 10 轉成本欄位（LinkLag 500 = 50% → lagMinutes 5000），
    // export.ts 再除以 10 寫回。因此此處除以 10000 還原成比例。
    // 見 scheduling-mspdi-roundtrip.test.ts 的跨模組驗證。
    const predecessor = graph.tasksById.get(dep.predecessorId as string);
    const predecessorDuration = (predecessor?.durationMinutes as number | undefined) ?? 0;
    return Math.round((predecessorDuration * raw) / 10000);
  };

  const shiftByLag = (
    calendar: ResolvedCalendar,
    at: ISODateTime,
    dep: Dependency,
  ): ISODateTime => {
    const lag = lagMinutesOf(dep);
    if (lag === 0) return at;
    const lagCalendar = dep.lagIsElapsed === true ? ELAPSED_CALENDAR : calendar;
    return lag > 0
      ? addWorkingMinutes(lagCalendar, at, minutes(lag))
      : subtractWorkingMinutes(lagCalendar, at, minutes(-lag));
  };

  const durationOf = (state: TaskState): number => {
    const value = state.task.durationMinutes as number;
    if (!Number.isFinite(value) || value < 0) {
      throw new InvalidArgumentError(
        `Task ${state.task.id} has an invalid durationMinutes: ${String(value)}`,
      );
    }
    return value;
  };

  const startPlus = (state: TaskState, from: ISODateTime, duration: number): ISODateTime =>
    duration === 0 ? from : addWorkingMinutes(state.calendar, from, minutes(duration));

  const finishMinus = (state: TaskState, from: ISODateTime, duration: number): ISODateTime =>
    duration === 0 ? from : subtractWorkingMinutes(state.calendar, from, minutes(duration));

  const conflictWarning = (
    state: TaskState,
    detail: string,
  ): void => {
    diagnostics.push({
      severity: 'Warning',
      code: 'CONSTRAINT_DEPENDENCY_CONFLICT',
      message: `Task ${state.task.id} (${state.task.name}): constraint ${state.task.constraintType} ${detail}`,
      taskId: state.task.id,
    });
  };

  // -------------------------------------------------------------------------
  // 正向計算（early start / early finish）
  // -------------------------------------------------------------------------

  const forwardPass = (defaultStart: ISODateTime): void => {
    for (const task of graph.topoOrder) {
      const state = stateOf(task.id as string);

      if (state.isSummary && rollupSummaries) {
        let start: ISODateTime | null = null;
        let finish: ISODateTime | null = null;
        for (const child of state.children) {
          const childState = stateOf(child.id as string);
          start = start === null ? childState.earlyStart : earlier(start, childState.earlyStart);
          finish = finish === null ? childState.earlyFinish : later(finish, childState.earlyFinish);
        }
        state.earlyStart = start ?? defaultStart;
        state.earlyFinish = finish ?? state.earlyStart;
        continue;
      }

      if (task.isManuallyScheduled) {
        state.earlyStart = task.start;
        state.earlyFinish = later(task.start, task.finish);
        continue;
      }

      const duration = durationOf(state);
      let startFromDeps: ISODateTime | null = null;
      let finishFromDeps: ISODateTime | null = null;

      for (const dep of graph.incomingByTask.get(task.id as string) ?? []) {
        const predecessor = stateOf(dep.predecessorId as string);
        switch (dep.type) {
          case 'FS': {
            const at = shiftByLag(state.calendar, predecessor.earlyFinish, dep);
            startFromDeps = startFromDeps === null ? at : later(startFromDeps, at);
            break;
          }
          case 'SS': {
            const at = shiftByLag(state.calendar, predecessor.earlyStart, dep);
            startFromDeps = startFromDeps === null ? at : later(startFromDeps, at);
            break;
          }
          case 'FF': {
            const at = shiftByLag(state.calendar, predecessor.earlyFinish, dep);
            finishFromDeps = finishFromDeps === null ? at : later(finishFromDeps, at);
            break;
          }
          case 'SF': {
            const at = shiftByLag(state.calendar, predecessor.earlyStart, dep);
            finishFromDeps = finishFromDeps === null ? at : later(finishFromDeps, at);
            break;
          }
          default: {
            const exhaustive: never = dep.type;
            throw new InvalidArgumentError(`Unknown dependency type ${String(exhaustive)}`);
          }
        }
      }

      let start = later(startFromDeps ?? defaultStart, defaultStart);
      if (finishFromDeps !== null) {
        start = later(start, finishMinus(state, finishFromDeps, duration));
      }
      if (duration > 0) start = snapToNextWorkingTime(state.calendar, start);
      let finish = startPlus(state, start, duration);

      const dependencyDrivenStart = start;
      const dependencyDrivenFinish = finish;
      const constraintDate = task.constraintDate;

      if (constraintDate !== null) {
        const setStart = (at: ISODateTime): void => {
          start = duration > 0 ? snapToNextWorkingTime(state.calendar, at) : at;
          finish = startPlus(state, start, duration);
        };
        const setFinish = (at: ISODateTime): void => {
          finish = duration > 0 ? snapToPreviousWorkingTime(state.calendar, at) : at;
          start = finishMinus(state, finish, duration);
        };
        const isHard =
          scheduleFrom === 'ProjectStart'
            ? HARD_FROM_START.has(task.constraintType)
            : task.constraintType === 'MSO' ||
              task.constraintType === 'MFO' ||
              task.constraintType === 'SNET' ||
              task.constraintType === 'FNET';

        switch (task.constraintType) {
          case 'SNET':
            if (isBefore(start, constraintDate)) setStart(constraintDate);
            break;
          case 'FNET':
            if (isBefore(finish, constraintDate)) setFinish(constraintDate);
            break;
          case 'SNLT':
            if (isAfter(start, constraintDate)) {
              conflictWarning(
                state,
                `requires start <= ${constraintDate as string} but dependencies drive it to ${dependencyDrivenStart as string}`,
              );
              if (honourConstraints && isHard) setStart(constraintDate);
            }
            break;
          case 'FNLT':
            if (isAfter(finish, constraintDate)) {
              conflictWarning(
                state,
                `requires finish <= ${constraintDate as string} but dependencies drive it to ${dependencyDrivenFinish as string}`,
              );
              if (honourConstraints && isHard) setFinish(constraintDate);
            }
            break;
          case 'MSO':
            if (!isSame(start, constraintDate)) {
              if (isAfter(dependencyDrivenStart, constraintDate)) {
                conflictWarning(
                  state,
                  `pins start to ${constraintDate as string} but dependencies drive it to ${dependencyDrivenStart as string}`,
                );
              }
              if (honourConstraints || isBefore(dependencyDrivenStart, constraintDate)) {
                setStart(constraintDate);
              }
            }
            break;
          case 'MFO':
            if (!isSame(finish, constraintDate)) {
              if (isAfter(dependencyDrivenFinish, constraintDate)) {
                conflictWarning(
                  state,
                  `pins finish to ${constraintDate as string} but dependencies drive it to ${dependencyDrivenFinish as string}`,
                );
              }
              if (honourConstraints || isBefore(dependencyDrivenFinish, constraintDate)) {
                setFinish(constraintDate);
              }
            }
            break;
          case 'ASAP':
          case 'ALAP':
            break;
          default: {
            const exhaustive: never = task.constraintType;
            throw new InvalidArgumentError(`Unknown constraint type ${String(exhaustive)}`);
          }
        }
      }

      state.earlyStart = start;
      state.earlyFinish = finish;
    }
  };

  // -------------------------------------------------------------------------
  // 逆向計算（late start / late finish）
  // -------------------------------------------------------------------------

  const backwardPass = (defaultFinish: ISODateTime): void => {
    for (let i = graph.topoOrder.length - 1; i >= 0; i -= 1) {
      const task = graph.topoOrder[i];
      if (task === undefined) continue;
      const state = stateOf(task.id as string);

      if (task.isManuallyScheduled) {
        state.lateStart = task.start;
        state.lateFinish = later(task.start, task.finish);
        continue;
      }

      let lateFinish: ISODateTime | null = null;
      for (const dep of graph.outgoingByTask.get(task.id as string) ?? []) {
        const successor = stateOf(dep.successorId as string);
        let candidate: ISODateTime;
        switch (dep.type) {
          case 'FS':
            // t.LF <= s.LS - lag（lag 一律以 successor 的日曆換算，與正向計算對稱）
            candidate = unshiftByLag(successor.calendar, successor.lateStart, dep);
            break;
          case 'FF':
            candidate = unshiftByLag(successor.calendar, successor.lateFinish, dep);
            break;
          case 'SS': {
            // t.LS <= s.LS - lag  =>  t.LF <= (s.LS - lag) + duration
            const ls = unshiftByLag(successor.calendar, successor.lateStart, dep);
            candidate = startPlus(state, ls, durationForLate(state));
            break;
          }
          case 'SF': {
            const ls = unshiftByLag(successor.calendar, successor.lateFinish, dep);
            candidate = startPlus(state, ls, durationForLate(state));
            break;
          }
          default: {
            const exhaustive: never = dep.type;
            throw new InvalidArgumentError(`Unknown dependency type ${String(exhaustive)}`);
          }
        }
        lateFinish = lateFinish === null ? candidate : earlier(lateFinish, candidate);
      }

      if (lateFinish === null) lateFinish = defaultFinish;

      // 階層：子任務不得晚於摘要任務（摘要在反向順序中先被處理）。
      if (task.parentId !== null && rollupSummaries) {
        const parent = states.get(task.parentId as string);
        if (parent !== undefined) lateFinish = earlier(lateFinish, parent.lateFinish);
      }

      /**
       * deadline 的套用位置取決於排程方向 —— 兩種模式下它的定位根本不同：
       *
       * - 逆排：late dates **就是**實際日期。若讓僅供警示的 deadline 覆寫
       *   MSO / MFO / SNET / FNET 這類硬性限制，硬性限制會被無聲破壞。
       *   因此 deadline 先套用，由硬性限制擁有最終決定權。
       * - 順排：實際日期由正向計算決定，late dates 只用來算浮時。deadline 必須是
       *   **最後一道上限**（見下方），否則 MSO / MFO 的「指派」會整個蓋掉 deadline，
       *   把應該呈現的負浮時掩蓋成 0。
       *
       * 對應測試：scheduling-deadline-matrix.test.ts。
       */
      const backwardMode = scheduleFrom === 'ProjectFinish';
      if (backwardMode && task.deadline !== null) {
        lateFinish = earlier(lateFinish, task.deadline);
      }

      const constraintDate = task.constraintDate;
      if (constraintDate !== null && !state.isSummary) {
        const duration = durationOf(state);
        const drivenLateFinish = lateFinish;
        /**
         * SNET / FNET 在逆排模式下是硬性限制：它們是 late finish 的**下限**。
         * 順排模式下它們只在正向計算生效（軟限制），逆向不得抬高 late finish，
         * 否則會把硬性限制或 deadline 造成的負浮時掩蓋掉。
         */
        const applyFloor = (floor: ISODateTime): void => {
          if (!isBefore(lateFinish as ISODateTime, floor)) return;
          if (!backwardMode || !honourConstraints) return;
          conflictWarning(
            state,
            `requires the task to finish no earlier than ${floor as string}, but successors drive its late finish to ${drivenLateFinish as string}`,
          );
          lateFinish = floor;
        };

        switch (task.constraintType) {
          case 'FNLT':
            lateFinish = earlier(lateFinish, constraintDate);
            break;
          case 'SNLT':
            lateFinish = earlier(lateFinish, startPlus(state, constraintDate, duration));
            break;
          case 'FNET':
            applyFloor(constraintDate);
            break;
          case 'SNET':
            applyFloor(startPlus(state, constraintDate, duration));
            break;
          case 'MFO':
            lateFinish = honourConstraints ? constraintDate : earlier(lateFinish, constraintDate);
            break;
          case 'MSO':
            lateFinish = honourConstraints
              ? startPlus(state, constraintDate, duration)
              : earlier(lateFinish, startPlus(state, constraintDate, duration));
            break;
          default:
            break;
        }
      }

      // 順排：deadline 是最後一道上限，套用在所有限制條件之後，永遠收得住 late finish。
      if (!backwardMode && task.deadline !== null) {
        lateFinish = earlier(lateFinish, task.deadline);
      }

      state.lateFinish = lateFinish;
      if (state.isSummary && rollupSummaries) {
        // 摘要的 late start 之後由子任務彙總覆寫，這裡只是暫定值。
        state.lateStart = lateFinish;
        continue;
      }

      const duration = durationOf(state);
      const snappedFinish =
        duration > 0 ? snapToPreviousWorkingTime(state.calendar, lateFinish) : lateFinish;
      state.lateFinish = snappedFinish;
      state.lateStart = finishMinus(state, snappedFinish, duration);
    }
  };

  /** 逆向時的 lag 反向位移（successor 時間點 − lag）。 */
  function unshiftByLag(
    calendar: ResolvedCalendar,
    at: ISODateTime,
    dep: Dependency,
  ): ISODateTime {
    const lag = lagMinutesOf(dep);
    if (lag === 0) return at;
    const lagCalendar = dep.lagIsElapsed === true ? ELAPSED_CALENDAR : calendar;
    return lag > 0
      ? subtractWorkingMinutes(lagCalendar, at, minutes(lag))
      : addWorkingMinutes(lagCalendar, at, minutes(-lag));
  }

  /** 逆向計算 SS/SF 時需要的工期；摘要在此階段尚未彙總，一律以 0 處理。 */
  function durationForLate(state: TaskState): number {
    if (state.isSummary && rollupSummaries) return 0;
    return durationOf(state);
  }

  /** 由子任務彙總摘要任務的 late start / late finish（需在 topo 正序執行）。 */
  const rollupLateDates = (): void => {
    for (const task of graph.topoOrder) {
      const state = stateOf(task.id as string);
      if (!state.isSummary || !rollupSummaries) continue;
      let lateStart: ISODateTime | null = null;
      let lateFinish: ISODateTime | null = null;
      for (const child of state.children) {
        const childState = stateOf(child.id as string);
        lateStart = lateStart === null ? childState.lateStart : earlier(lateStart, childState.lateStart);
        lateFinish =
          lateFinish === null ? childState.lateFinish : later(lateFinish, childState.lateFinish);
      }
      if (lateStart !== null) state.lateStart = lateStart;
      if (lateFinish !== null) state.lateFinish = lateFinish;
    }
  };

  // -------------------------------------------------------------------------
  // 驅動兩個方向
  // -------------------------------------------------------------------------

  if (scheduleFrom === 'ProjectFinish') {
    // 時序很重要：late dates 必須在 forward pass **之後**才複製到實際日期，
    // 否則正向計算套用的限制條件（SNET / FNET / MSO / MFO）永遠傳不到 start / finish。
    // 1) 逆向計算 late dates（含 SNET / FNET 硬性下限）。
    backwardPass(anchorDate);
    rollupLateDates();
    // 2) 由 late start 決定專案起點，不經過 state.start。
    let projectStart: ISODateTime | null = null;
    for (const state of states.values()) {
      projectStart =
        projectStart === null ? state.lateStart : earlier(projectStart, state.lateStart);
    }
    // 3) 正向計算 early dates（同樣套用限制條件）。
    forwardPass(projectStart ?? anchorDate);
    // 4) 才複製到實際日期：逆排預設走 late dates（ALAP）；但若限制條件把 early dates
    //    推得比 late dates 還晚（例如硬性 SNET 擠開了後續任務），該任務不可能排在
    //    late dates，改採 early 這一組，start / finish 才會是一致的配對。
    for (const state of states.values()) {
      const useEarly = isAfter(state.earlyStart, state.lateStart);
      state.start = useEarly ? state.earlyStart : state.lateStart;
      state.finish = useEarly ? state.earlyFinish : state.lateFinish;
    }
    // 5) 子任務可能因此位移，重新彙總摘要的實際日期。
    if (rollupSummaries) {
      for (const task of graph.topoOrder) {
        const state = stateOf(task.id as string);
        if (!state.isSummary) continue;
        let start: ISODateTime | null = null;
        let finish: ISODateTime | null = null;
        for (const child of state.children) {
          const childState = stateOf(child.id as string);
          start = start === null ? childState.start : earlier(start, childState.start);
          finish = finish === null ? childState.finish : later(finish, childState.finish);
        }
        if (start !== null) state.start = start;
        if (finish !== null) state.finish = finish;
      }
    }
  } else {
    forwardPass(anchorDate);
    for (const state of states.values()) {
      state.start = state.earlyStart;
      state.finish = state.earlyFinish;
    }
    let projectFinish: ISODateTime | null = null;
    for (const state of states.values()) {
      projectFinish = projectFinish === null ? state.finish : later(projectFinish, state.finish);
    }
    backwardPass(projectFinish ?? anchorDate);
    rollupLateDates();
    // ALAP：順排模式下把任務推到最晚可行的位置。
    for (const state of states.values()) {
      if (state.task.constraintType !== 'ALAP') continue;
      if (state.task.isManuallyScheduled || state.isSummary) continue;
      state.start = state.lateStart;
      state.finish = state.lateFinish;
    }
    // ALAP 可能改變摘要的彙總結果，重新彙總一次。
    if (rollupSummaries) {
      for (const task of graph.topoOrder) {
        const state = stateOf(task.id as string);
        if (!state.isSummary) continue;
        let start: ISODateTime | null = null;
        let finish: ISODateTime | null = null;
        for (const child of state.children) {
          const childState = stateOf(child.id as string);
          start = start === null ? childState.start : earlier(start, childState.start);
          finish = finish === null ? childState.finish : later(finish, childState.finish);
        }
        if (start !== null) state.start = start;
        if (finish !== null) state.finish = finish;
      }
    }
  }

  // -------------------------------------------------------------------------
  // 浮時、要徑、診斷
  // -------------------------------------------------------------------------

  for (const state of states.values()) {
    state.totalFloat = workingMinutesBetween(
      state.calendar,
      state.earlyStart,
      state.lateStart,
    ) as number;

    let free: number | null = null;
    for (const dep of graph.outgoingByTask.get(state.task.id as string) ?? []) {
      const successor = stateOf(dep.successorId as string);
      const drivingPoint = dep.type === 'FS' || dep.type === 'FF' ? state.earlyFinish : state.earlyStart;
      const targetPoint = dep.type === 'FS' || dep.type === 'SS' ? successor.earlyStart : successor.earlyFinish;
      const required = shiftByLag(successor.calendar, drivingPoint, dep);
      const slack = workingMinutesBetween(successor.calendar, required, targetPoint) as number;
      free = free === null ? slack : Math.min(free, slack);
    }
    state.freeFloat = free === null ? state.totalFloat : Math.min(free, state.totalFloat);
    state.isCritical = state.totalFloat <= criticalThreshold;
  }

  const deadlineViolations: TaskId[] = [];
  for (const task of project.tasks) {
    const state = stateOf(task.id as string);
    if (task.deadline !== null && isAfter(state.finish, task.deadline)) {
      deadlineViolations.push(task.id);
      diagnostics.push({
        severity: 'Warning',
        code: 'DEADLINE_VIOLATION',
        message: `Task ${task.id} (${task.name}) finishes at ${state.finish as string}, after its deadline ${task.deadline as string}.`,
        taskId: task.id,
      });
    }
    if (state.isSummary && rollupSummaries) {
      if ((task.durationMinutes as number) !== 0) {
        diagnostics.push({
          severity: 'Info',
          code: 'SUMMARY_DURATION_IGNORED',
          message: `Summary task ${task.id} (${task.name}) has an explicit duration; it is derived from its children instead.`,
          taskId: task.id,
        });
      }
      if (task.constraintType !== 'ASAP' && task.constraintType !== 'ALAP') {
        diagnostics.push({
          severity: 'Warning',
          code: 'SUMMARY_CONSTRAINT_IGNORED',
          message: `Summary task ${task.id} (${task.name}) has constraint ${task.constraintType}; constraints on summary tasks are not applied to the rollup.`,
          taskId: task.id,
        });
      }
      const incoming = graph.incomingByTask.get(task.id as string) ?? [];
      if (incoming.length > 0) {
        diagnostics.push({
          severity: 'Warning',
          code: 'SUMMARY_FINISH_DEPENDENCY_IGNORED',
          message: `Summary task ${task.id} (${task.name}) has FF/SF predecessors; those cannot move a rolled-up summary and were ignored.`,
          taskId: task.id,
        });
      }
    }
    if (state.totalFloat < 0) {
      if (!allowNegativeFloat) {
        throw new ConstraintConflictError({
          taskId: task.id,
          constraintType: task.constraintType,
          constraintDate: task.constraintDate,
          conflictingTaskIds: (graph.incomingByTask.get(task.id as string) ?? []).map(
            (dep) => dep.predecessorId,
          ),
          detail: `total float is ${state.totalFloat} minutes`,
        });
      }
      diagnostics.push({
        severity: 'Warning',
        code: 'NEGATIVE_FLOAT',
        message: `Task ${task.id} (${task.name}) has negative total float (${state.totalFloat} minutes).`,
        taskId: task.id,
      });
    }
  }

  // --- 輸出 ---
  const scheduled: ScheduledTask[] = [];
  let projectStart: ISODateTime | null = null;
  let projectFinish: ISODateTime | null = null;
  for (const task of project.tasks) {
    const state = stateOf(task.id as string);
    projectStart = projectStart === null ? state.start : earlier(projectStart, state.start);
    projectFinish = projectFinish === null ? state.finish : later(projectFinish, state.finish);
    scheduled.push({
      taskId: task.id,
      start: state.start,
      finish: state.finish,
      earlyStart: state.earlyStart,
      earlyFinish: state.earlyFinish,
      lateStart: state.lateStart,
      lateFinish: state.lateFinish,
      totalFloat: minutes(state.totalFloat),
      freeFloat: minutes(state.freeFloat),
      isCritical: state.isCritical,
      durationMinutes: workingMinutesBetween(state.calendar, state.start, state.finish),
      segments: [{ start: state.start, finish: state.finish }],
    });
  }

  const criticalPath = scheduled
    .filter((entry) => entry.isCritical)
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      if (isBefore(a.entry.earlyStart, b.entry.earlyStart)) return -1;
      if (isAfter(a.entry.earlyStart, b.entry.earlyStart)) return 1;
      if (isBefore(a.entry.earlyFinish, b.entry.earlyFinish)) return -1;
      if (isAfter(a.entry.earlyFinish, b.entry.earlyFinish)) return 1;
      return a.index - b.index;
    })
    .map((wrapped) => wrapped.entry.taskId);

  return {
    tasks: scheduled,
    projectStart: projectStart ?? anchorDate,
    projectFinish: projectFinish ?? anchorDate,
    criticalPath,
    deadlineViolations,
    diagnostics,
  };
}

// ---------------------------------------------------------------------------
// 套用結果
// ---------------------------------------------------------------------------

/** 將排程結果套回專案，回傳新的 Project（不可變更新）。 */
export function applySchedule(project: Project, result: ScheduleResult): Project {
  const byId = new Map<string, ScheduledTask>();
  for (const entry of result.tasks) byId.set(entry.taskId as string, entry);

  const childrenByParent = new Map<string, Task[]>();
  for (const task of project.tasks) {
    if (task.parentId === null) continue;
    const bucket = childrenByParent.get(task.parentId as string);
    if (bucket === undefined) childrenByParent.set(task.parentId as string, [task]);
    else bucket.push(task);
  }

  const updated = new Map<string, Task>();
  const visiting = new Set<string>();

  const applyTo = (task: Task): Task => {
    const cached = updated.get(task.id as string);
    if (cached !== undefined) return cached;
    if (visiting.has(task.id as string)) {
      // 階層成環（schedule() 會拒絕這種輸入，這裡只是防止無限遞迴）。
      return task;
    }
    visiting.add(task.id as string);
    const entry = byId.get(task.id as string);
    if (entry === undefined) {
      updated.set(task.id as string, task);
      return task;
    }
    const children = childrenByParent.get(task.id as string) ?? [];
    const isSummary = children.length > 0;

    let percentComplete = task.percentComplete;
    let work = task.work;
    if (isSummary) {
      // 摘要進度的加權規則與進度回報流程共用（見 task-progress.ts）。
      const resolvedChildren = children.map((child) => applyTo(child));
      work = minutes(resolvedChildren.reduce((sum, child) => sum + (child.work as number), 0));
      percentComplete = rollUpSummaryPercent(
        resolvedChildren,
        (child) => child.percentComplete,
      ) as Task['percentComplete'];
    }

    const next: Task = {
      ...task,
      start: entry.start,
      finish: entry.finish,
      earlyStart: entry.earlyStart,
      earlyFinish: entry.earlyFinish,
      lateStart: entry.lateStart,
      lateFinish: entry.lateFinish,
      totalFloat: entry.totalFloat,
      freeFloat: entry.freeFloat,
      isCritical: entry.isCritical,
      durationMinutes: entry.durationMinutes,
      isSummary,
      work,
      percentComplete,
    };
    visiting.delete(task.id as string);
    updated.set(task.id as string, next);
    return next;
  };

  const tasks = project.tasks.map((task) => applyTo(task));
  const assignments = project.assignments.map((assignment) => {
    const entry = byId.get(assignment.taskId as string);
    // 指派的時段衍生自所屬任務。同步日期時保留實際工時、輪廓與撫平延遲等
    // 指派層級狀態；timephased 的切片帶有舊日期，必須失效後由資源／成本引擎重算。
    // 手動排程任務的 entry 則正是其手動日期。
    if (
      entry === undefined ||
      (assignment.start === entry.start && assignment.finish === entry.finish)
    ) {
      return assignment;
    }
    return {
      ...assignment,
      start: entry.start,
      finish: entry.finish,
      timephased: null,
    };
  });

  return {
    ...project,
    tasks,
    assignments,
    startDate: result.projectStart,
    finishDate: result.projectFinish,
  };
}
